/** @jsx jsx */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import { Typography, breakpoints } from '@coursera/cds-core';

import type { UniversityProgramPageContentTestimonialsCollection } from 'bundles/xddp/__generated__/baseContentfulTypes';
import TestimonialCard from 'bundles/xddp/components/homepage/TestimonialCard';
import { TestimonialCardsVariant } from 'bundles/xddp/constants';

import _t from 'i18n!nls/xddp';

const styles: Record<string, Interpolation<Theme>> = {
  testimonialContainer: () => css`
    margin: var(--cds-spacing-400) auto;
    max-width: ${breakpoints.values.lg}px;
  `,
  testimonialCardsSection: () => css`
    margin: var(--cds-spacing-600) 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    ${breakpoints.down(breakpoints.values.lg)} {
      margin: var(--cds-spacing-150) 0;
    }
    ${breakpoints.down(breakpoints.values.md)} {
      flex-direction: column;
      margin: var(--cds-spacing-600) var(--cds-spacing-300);
    }
  `,
  headerText: () => css`
    margin-bottom: var(--cds-spacing-200);
    font-size: 32px;
    text-align: center;
  `,
};

type Props = {
  testimonialsCollection?: UniversityProgramPageContentTestimonialsCollection;
};

const TestimonialSection: React.FC<Props> = ({ testimonialsCollection }) => {
  if (!testimonialsCollection?.items.length) {
    return null;
  }
  const testimonialCardVariant =
    testimonialsCollection?.items.length <= 2 ? TestimonialCardsVariant.DOUBLE : TestimonialCardsVariant.TRIPLE;
  return (
    <div css={styles.testimonialContainer} data-testid="testimonials-section-container">
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h2semibold" css={styles.headerText}>
        {_t('Hear from our learners')}
      </Typography>
      <div css={styles.testimonialCardsSection} data-testid="testimonials-collection">
        {testimonialsCollection.items.map((testimonial) => {
          const { image, authorName, productName, text, productTypes, productSlug } = testimonial || {};
          return (
            <TestimonialCard
              key={authorName}
              image={image}
              authorName={authorName}
              productName={productName}
              text={text}
              productTypes={productTypes}
              productSlug={productSlug}
              variant={testimonialCardVariant}
            />
          );
        })}
      </div>
    </div>
  );
};

export default TestimonialSection;
